import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AssessmentV2Provider, useAssessmentV2, useEnsureSession } from "@/features/assessment-v2/store";
import { ProgressBar } from "@/features/assessment-v2/components/ProgressBar";
import { ChapterCard } from "@/features/assessment-v2/components/ChapterCard";
import { JourneyDock } from "@/features/assessment-v2/components/JourneyDock";

import { CompanionNote } from "@/features/assessment-v2/components/CompanionNote";
import { XpPill } from "@/features/assessment-v2/components/XpPill";
import { SaveStatus } from "@/features/assessment-v2/components/SaveStatus";
import { yomaFor, xpForWalk } from "@/features/assessment-v2/delight";
import {
  activeScreens, chapterForScreen, nextScreen, prevScreen,
} from "@/features/assessment-v2/chapters";
import { parseBand } from "@/features/assessment-v2/band";
import { useSearchParams } from "react-router-dom";
import {
  QualificationScreen,
  StudyAppetiteScreen,
  RealityCheckScreen,
  SalaryFloorScreen,
  SalaryFlexScreen,
  ExperienceLocationScreen, BackgroundScreen
} from "@/features/assessment-v2/screens/Part1Screens";
import {
  StreamScreen,
  StreamLeanScreen,
  MoneyMattersScreen,
  StudyMobilityScreen,
} from "@/features/assessment-v2/screens/Part1TeenScreens";
import {
  SchoolYearScreen,
  WhatYouLikeScreen,
} from "@/features/assessment-v2/screens/Part1ExplorerScreens";
import { ChildhoodDreamScreen, CareerPullScreen } from "@/features/assessment-v2/screens/CareerPullScreens";
import { PassionsScreen } from "@/features/assessment-v2/screens/PassionsScreen";
import { HobbyFrequencyScreen } from "@/features/assessment-v2/screens/HobbyFrequencyScreen";
import { CauseRankScreen } from "@/features/assessment-v2/screens/CauseRankScreen";
import { CauseRoleScreen } from "@/features/assessment-v2/screens/CauseRoleScreen";
import { EndOfPart1Screen } from "@/features/assessment-v2/screens/EndOfPart1Screen";
import { IntroScreen } from "@/features/assessment-v2/screens/IntroScreen";
import { QuizBlockScreen, TraitBlockScreen } from "@/features/assessment-v2/screens/V1PortScreens";
import { SkillsEvidenceScreen } from "@/features/assessment-v2/screens/SkillsEvidenceScreen";
import { SKILLS_SCREEN_IDS } from "@/features/assessment-v2/skillsEvidence";
import { QUIZ_SCREEN_IDS, TRAIT_SCREEN_IDS } from "@/features/assessment-v2/v1port/blocks";

import {
  WorkdaySortScreen,
  TwoOffersScreen,
  TradeOffsScreen,
  QuickOnesScreen,
  LastMonthScreen,
  GrindTestScreen,
  CollapseScreen,
  BadDayScreen,
  WhoseProblemScreen,
  RoomScreen,
  FocusSliderScreen,
  PeopleBatteryScreen,
  HardDayScreen,
  WorkingPatternScreen,
  PresentingScreen,
  TenYearsScreen,
  ScenarioDeadlineScreen,
  ScenarioMonthOffScreen,
  ScenarioFailureScreen,
  ScenarioTeammateScreen,
  ScenarioOpenWeekScreen,
  CreativeRoomScreen,
  ReviewConfirmScreen,
} from "@/features/assessment-v2/screens/Part2Screens";
import type { Chapter } from "@/features/assessment-v2/types";
import { ChevronLeft } from "lucide-react";
import yomaAvatar from "@/assets/yoma-avatar.png.asset.json";

/**
 * WCC360 v2 — the assessment runner. One component tree, two age bands.
 *
 * Full-screen, one interaction per screen, dark, mobile-first.
 * Never surfaces "step N of M". Chapter names only.
 *
 * The band comes from `?band=15-18` and selects the item set (chapters.ts)
 * and the wording (copy.ts). It must never select a different component.
 */
function AssessmentV2Inner() {
  const { state, goto, band } = useAssessmentV2();
  const screenId = state.currentScreen;

  // Conditional screens (X-STREAM-002 only fires for an undecided stream)
  // mean the served list is shorter than the authored list.
  const served = useMemo(() => activeScreens(state), [state]);
  const lastPart1 = served.filter((s) => s.startsWith("p1_")).slice(-1)[0] ?? served[0];

  const chapter = useMemo(() => chapterForScreen(screenId, band), [screenId, band]);
  const [shownChapter, setShownChapter] = useState<Chapter | null>(null);
  // A ref, not state: marking a chapter as seen must not re-run the effect,
  // or the cleanup cancels the 3s auto-dismiss and the card sticks forever.
  const seenChapters = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!chapter) return;
    if (seenChapters.current.has(chapter.id)) return;
    seenChapters.current.add(chapter.id);
    setShownChapter(chapter);
    const t = window.setTimeout(() => setShownChapter(null), 3000);
    return () => window.clearTimeout(t);
  }, [chapter]);


  const advance = useCallback(() => {
    const next = nextScreen(screenId, state);
    // Every authored screen is built, so the only exit is the end of the walk.
    if (!next) { goto("__end_part1__"); return; }
    goto(next);
  }, [screenId, goto, state]);

  const goBack = useCallback(() => {
    if (screenId === "__intro__") return;
    if (screenId === served[0]) { goto("__intro__"); return; }
    if (screenId === "__end_part1__") { goto(lastPart1); return; }
    const prev = prevScreen(screenId, state);
    if (prev) goto(prev);
  }, [screenId, goto, state, served, lastPart1]);

  const isIntro = screenId === "__intro__";

  // Scroll and next step: every new screen starts at the top of the page, so a
  // long screen never leaves the next question half-read below the fold.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, [screenId]);


  // XP is presentation only: one award per decision already made, derived
  // from position in the served walk by the single rule in delight.ts.
  // Nothing here is stored, scored or sent to the engine.
  const xp = xpForWalk(screenId, served);
  const [gained, setGained] = useState<number | null>(null);
  const prevXp = useRef(xp);
  useEffect(() => {
    if (xp > prevXp.current) {
      setGained(xp - prevXp.current);
      const t = window.setTimeout(() => setGained(null), 750);
      prevXp.current = xp;
      return () => window.clearTimeout(t);
    }
    prevXp.current = xp;
  }, [xp]);

  /**
   * FOCUS MODE. One decision on screen at a time, and everything that is
   * not that decision gets out of the way: YoMA's nudge lands with the
   * chapter, then quietly folds away after a few seconds. The learner can
   * pull her back any time — the guide is never actually gone.
   */
  const [guideOpen, setGuideOpen] = useState(true);
  useEffect(() => {
    if (!chapter) return;
    setGuideOpen(true);
    const t = window.setTimeout(() => setGuideOpen(false), 7000);
    return () => window.clearTimeout(t);
  }, [chapter]);

  // The durable session is created the moment the learner leaves the intro,
  // so a run that is abandoned on the first screen leaves nothing behind.
  useEnsureSession(band, !isIntro);



  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      {!isIntro && (
        <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border">
          <div className="max-w-3xl mx-auto px-5 md:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={goBack}
                aria-label="Back"
                className="h-9 w-9 grid place-items-center rounded-full border border-border
                  text-muted-foreground hover:text-foreground hover:bg-secondary
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex-1">
                <ProgressBar
                  screenId={screenId === "__end_part1__" ? lastPart1 : screenId}
                  band={band}
                  state={state}
                />
              </div>
              <XpPill xp={xp} gained={gained} className="shrink-0" />
              <SaveStatus className="shrink-0" />
              <button
                onClick={() => setGuideOpen((v) => !v)}
                aria-expanded={guideOpen}
                aria-label={guideOpen ? "Hide YoMA's note" : "Ask YoMA"}
                className="h-9 w-9 shrink-0 grid place-items-center rounded-full border border-border
                  text-muted-foreground hover:text-foreground hover:bg-secondary
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <img
                  src={yomaAvatar.url}
                  alt=""
                  aria-hidden
                  className="h-6 w-6 rounded-full object-cover"
                />
              </button>
            </div>
            {/* YoMA stays present through the chapter, not only on the card —
                but in focus mode she folds away once she's been read. */}
            <AnimatePresence initial={false}>
              {guideOpen && yomaFor(chapter?.id) && (
                <motion.div
                  key={chapter?.id}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pl-12">
                    <CompanionNote line={yomaFor(chapter?.id)} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

          </div>
        </header>
      )}

      <main className="flex-1 flex items-start md:items-center">
        <div className="w-full px-5 md:px-8 py-10 md:py-16 pb-32 md:pb-36">
          <AnimatePresence mode="wait">
            <motion.div
              key={screenId}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <ScreenSwitch screenId={screenId} onNext={advance} />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {!isIntro && (
        <JourneyDock
          screenId={screenId === "__end_part1__" ? lastPart1 : screenId}
          band={band}
          state={state}
          onBack={goBack}
        />
      )}

      <AnimatePresence>
        {shownChapter && (
          <ChapterCard chapter={shownChapter} onDone={() => setShownChapter(null)} />
        )}
      </AnimatePresence>

    </div>
  );
}

/**
 * Every screen id `ScreenSwitch` handles. Kept next to the switch so the flow
 * test can assert coverage without rendering the tree.
 */
export const HANDLED_SCREEN_IDS = [
  "p1_qualification",
  "p1_study_appetite",
  "p1_reality_check",
  "p1_salary_floor",
  "p1_salary_flex",
  "p1_experience_location",
  "p1_background",
  "p1_stream",
  "p1_stream_lean",
  "p1_money_matters",
  "p1_study_mobility",
  "p1_school_year",
  "p1_what_you_like",

  "p2_workday_sort",
  "p2_two_offers",
  "p2_trade_offs",
  "p2_quick_ones",
  "p2_last_month",
  "p2_grind_test",
  "p2_collapse",
  "p2_bad_day",
  "p2_whose_problem",
  "p2_room",
  "p2_focus_slider",
  "p2_people_battery",
  "p2_hard_day",
  "p2_working_pattern",
  "p2_presenting",
  "p2_ten_years",
  "p2_scenario_deadline",
  "p2_scenario_month_off",
  "p2_scenario_failure",
  "p2_scenario_teammate",
  "p2_scenario_open_week",
  "p2_creative_room",
  "p2_here_is_what",
  "px_passions",
  "px_hobby_frequency",
  "px_cause_rank",
  "px_cause_role",
  "px_childhood_dream",
  "px_career_pull",
  ...SKILLS_SCREEN_IDS,
  ...TRAIT_SCREEN_IDS,
  ...QUIZ_SCREEN_IDS,
  "__intro__",
  "__end_part1__",
] as const;

function ScreenSwitch({ screenId, onNext }: { screenId: string; onNext: () => void }) {
  // Ported v1 instrument. Its screens are derived from the item bank, so they
  // are matched by prefix before the switch rather than enumerated in it.
  if (TRAIT_SCREEN_IDS.includes(screenId)) {
    return <TraitBlockScreen screenId={screenId} onNext={onNext} />;
  }
  if (QUIZ_SCREEN_IDS.includes(screenId)) {
    return <QuizBlockScreen screenId={screenId} onNext={onNext} />;
  }
  // Skills Intelligence™ evidence — one component, several screens, each
  // rendering its own slice of the frozen evidence bank.
  if (SKILLS_SCREEN_IDS.includes(screenId)) {
    return <SkillsEvidenceScreen screenId={screenId} onNext={onNext} />;
  }
  switch (screenId) {
    // Part 1 — 18+
    case "p1_qualification":       return <QualificationScreen onNext={onNext} />;
    case "p1_study_appetite":      return <StudyAppetiteScreen onNext={onNext} />;
    case "p1_reality_check":       return <RealityCheckScreen onNext={onNext} />;
    case "p1_salary_floor":        return <SalaryFloorScreen onNext={onNext} />;
    case "p1_salary_flex":         return <SalaryFlexScreen onNext={onNext} />;
    case "p1_experience_location": return <ExperienceLocationScreen onNext={onNext} />;
    case "p1_background":          return <BackgroundScreen onNext={onNext} />;
    // Part 1 — 15–18. Stream replaces qualification, money is relative only,
    // and study mobility replaces the adult work-location question.
    case "p1_stream":              return <StreamScreen onNext={onNext} />;
    case "p1_stream_lean":         return <StreamLeanScreen onNext={onNext} />;
    case "p1_money_matters":       return <MoneyMattersScreen onNext={onNext} />;
    case "p1_study_mobility":      return <StudyMobilityScreen onNext={onNext} />;
    // Part 1 — 12–14. Two screens, neither of them scored.
    case "p1_school_year":         return <SchoolYearScreen onNext={onNext} />;
    case "p1_what_you_like":       return <WhatYouLikeScreen onNext={onNext} />;



    // Part 2 — how you work
    case "p2_workday_sort":        return <WorkdaySortScreen onNext={onNext} />;
    case "p2_two_offers":          return <TwoOffersScreen onNext={onNext} />;
    case "p2_trade_offs":          return <TradeOffsScreen onNext={onNext} />;
    case "p2_quick_ones":          return <QuickOnesScreen onNext={onNext} />;
    case "p2_last_month":          return <LastMonthScreen onNext={onNext} />;
    case "p2_grind_test":          return <GrindTestScreen onNext={onNext} />;
    case "p2_collapse":            return <CollapseScreen onNext={onNext} />;
    // Part 2 — what you'd carry (partial: screens 9–13)
    case "p2_bad_day":             return <BadDayScreen onNext={onNext} />;
    case "p2_whose_problem":       return <WhoseProblemScreen onNext={onNext} />;
    case "p2_room":                return <RoomScreen onNext={onNext} />;
    case "p2_focus_slider":        return <FocusSliderScreen onNext={onNext} />;
    case "p2_people_battery":      return <PeopleBatteryScreen onNext={onNext} />;
    case "p2_hard_day":            return <HardDayScreen onNext={onNext} />;
    case "p2_working_pattern":     return <WorkingPatternScreen onNext={onNext} />;
    case "p2_presenting":          return <PresentingScreen onNext={onNext} />;
    // Part 2 — where you're going
    case "p2_ten_years":           return <TenYearsScreen onNext={onNext} />;
    case "p2_scenario_deadline":   return <ScenarioDeadlineScreen onNext={onNext} />;
    case "p2_scenario_month_off":  return <ScenarioMonthOffScreen onNext={onNext} />;
    case "p2_scenario_failure":    return <ScenarioFailureScreen onNext={onNext} />;
    case "p2_scenario_teammate":   return <ScenarioTeammateScreen onNext={onNext} />;
    case "p2_scenario_open_week":  return <ScenarioOpenWeekScreen onNext={onNext} />;
    case "p2_creative_room":       return <CreativeRoomScreen onNext={onNext} />;
    // Shared by all three bands. Preference, not virtue — safe to ask directly.
    case "px_passions":            return <PassionsScreen onNext={onNext} />;
    case "px_hobby_frequency":     return <HobbyFrequencyScreen onNext={onNext} />;
    case "px_cause_rank":          return <CauseRankScreen onNext={onNext} />;
    case "px_cause_role":          return <CauseRoleScreen onNext={onNext} />;
    // "Your career matches" — stated interest, scored but tightly bounded.
    case "px_childhood_dream":     return <ChildhoodDreamScreen onNext={onNext} />;
    case "px_career_pull":         return <CareerPullScreen onNext={onNext} />;
    case "p2_here_is_what":        return <ReviewConfirmScreen />;
    // Ported v1 instrument — trait blocks and the three Nibble quizzes.
    // Block screens are generated from the item bank, so they match by prefix
    // instead of being listed one by one.
    case "__intro__":              return <IntroScreen />;
    case "__end_part1__":          return <EndOfPart1Screen />;
    default:
      return (
        <div className="max-w-xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">
            Let's get you back on track
          </div>
          <h1 className="font-display text-3xl font-medium">
            We lost your place for a second.
          </h1>
          <p className="mt-3 text-muted-foreground">
            Nothing you've said is gone. Head back with the arrow above and
            you'll pick up right where you were.
          </p>
        </div>
      );
  }
}

export default function AssessmentV2() {
  const [params] = useSearchParams();
  // `?band=15-18` picks the item set and the wording. Anything else — or
  // nothing — is the adult band.
  const band = parseBand(params.get("band"));
  return (
    <AssessmentV2Provider key={band} band={band}>
      <AssessmentV2Inner />
    </AssessmentV2Provider>
  );
}

